import { FiChevronDown, FiRefreshCcw } from "react-icons/fi";

import type {
  DataAssetTreeDirectoryResponse,
  DataAssetWithManifestAndRealAssetDeploymentResponse,
  DirectoryInventoryListItem,
} from "@/apiClient";
import { renderDeploymentName } from "@/apiUtils";

import ExpandedDirectoryContents from "./ExpandedDirectoryContents";

type PreviewDirectoryListItem = DirectoryInventoryListItem;

function PreviewExampleAccordion({
  selectedMatch,
  isOpen,
  onToggle,
  matchCount,
  onShowNextMatch,
  observedSubdirectories,
  observedFiles,
  isLoading,
  isEmpty,
}: {
  selectedMatch: PreviewDirectoryListItem | null;
  isOpen: boolean;
  onToggle: () => void;
  matchCount: number;
  onShowNextMatch: () => void;
  observedSubdirectories: DataAssetTreeDirectoryResponse[];
  observedFiles: DataAssetWithManifestAndRealAssetDeploymentResponse[];
  isLoading: boolean;
  isEmpty: boolean;
}) {
  if (!selectedMatch) {
    return null;
  }

  const deploymentLabel = selectedMatch.cruise_real_asset_deployment
    ? renderDeploymentName(selectedMatch.cruise_real_asset_deployment)
    : null;

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      <button
        type="button"
        className={`flex w-full items-center gap-2 px-4 py-3 text-left text-sm transition hover:bg-slate-100 ${
          isOpen ? "border-b border-slate-200" : ""
        }`}
        onClick={onToggle}
      >
        <FiChevronDown
          aria-hidden="true"
          className={`text-grey-200 transition-transform ${
            isOpen ? "" : "-rotate-90"
          }`}
          size={16}
        />
        <span className="shrink-0 font-medium text-grey-300">
          {deploymentLabel ? "Example match from" : "Example match"}
        </span>
        {deploymentLabel ? (
          <span className="min-w-0 break-all font-bold text-blue-400">
            {deploymentLabel}
          </span>
        ) : (
          <span className="min-w-0 break-all font-semibold text-blue-400">
            {selectedMatch.path}
          </span>
        )}
      </button>

      {isOpen ? (
        <div className="space-y-3 px-4 py-3">
          <ExpandedDirectoryContents
            rootPath={selectedMatch.path ?? ""}
            observedSubdirectories={observedSubdirectories}
            observedFiles={observedFiles}
            isLoading={isLoading}
            isEmpty={isEmpty}
          />

          {matchCount > 1 ? (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-1 py-0.5 text-xs text-grey-200 transition hover:text-blue-300"
              onClick={onShowNextMatch}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-grey-200">
                <FiRefreshCcw aria-hidden="true" size={13} />
              </span>
              Click to see another example match
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default PreviewExampleAccordion;
